import { EditorType } from "@kaggle/sdk/kernels";
import { createTheme, SessionState as StyledSessionState } from "@kaggle/core";
import { createSelector } from "reselect";
import { SidebarViewMetadata } from "../../types/SidebarViewMetadata";
import { ViewType } from "../../types/ViewType";
import { AppState } from "../types";
import { SharedState } from "../types/SharedState";
import { getCurrentIsOwner } from "./current";
import { getIsAdmin, getIsAnonymous } from "./user";
import { getIsCompetitionMetric } from "./category";

/** Selects the session state from the parent frame. */
export const getSessionState = (state: SharedState) => state.session;

/** Selects the default session settings from app state. */
export const getDefaultSessionSettings = (state: AppState) =>
  state.defaultSessionSettings;

/** Selects the source type of "script" or "notebook" in the current session. */
export const getSourceType = createSelector(
  [getSessionState],
  session => session.sourceType
);

export const getStyledSessionState = createSelector(
  [getSessionState],
  (session): StyledSessionState => ({
    kernelSourceType: getSourceTypeFromEditorType(session.sourceType),
    themeName: session.themeName,
  })
);

function getSourceTypeFromEditorType(editorType: EditorType) {
  switch (editorType) {
    case EditorType.SCRIPT:
      return "script";
    case EditorType.NOTEBOOK:
      return "notebook";
    default:
      return "unspecified";
  }
}

/** Creates the theme based on session information. */
export const getTheme = createSelector([getStyledSessionState], session =>
  createTheme(session)
);

/** Gets the editor settings from state. */
export const getDeviceSettings = createSelector(
  [getSessionState],
  session => session.settings
);

/** Gets the editor settings view (visibility) from state. */
export const getDeviceSettingsEnabledViews = createSelector(
  [getDeviceSettings],
  settings => settings.enabledViews
);

/** Gets the editor settings view (visibility) from state. */
export const getDeviceSettingsExpandedViews = createSelector(
  [getDeviceSettings],
  settings => settings.expandedViews
);

/** Gets whether the data pane is visible in sidebar. */
export const getIsDataPaneEnabled = createSelector(
  [getDeviceSettingsEnabledViews],
  views => views.includes(ViewType.DATA_PANE)
);

/** Gets whether the data pane is expanded in sidebar. */
export const getIsDataPaneExpanded = createSelector(
  [getDeviceSettingsExpandedViews],
  views => views.includes(ViewType.DATA_PANE)
);

/** Gets whether the models pane is visible in sidebar. */
export const getIsModelsPaneEnabled = createSelector(
  [getDeviceSettingsEnabledViews],
  views => views.includes(ViewType.MODELS_PANE)
);

/** Gets whether the models pane is expanded in sidebar. */
export const getIsModelsPaneExpanded = createSelector(
  [getDeviceSettingsExpandedViews],
  views => views.includes(ViewType.MODELS_PANE)
);

/** Gets whether the competitions pane is visible in sidebar. */
export const getIsCompetitionsPaneEnabled = createSelector(
  [getDeviceSettingsEnabledViews, getIsAnonymous],
  (views, isAnonymous) =>
    !isAnonymous && views.includes(ViewType.COMPETITIONS_PANE)
);

/** Gets whether the competitions pane is expanded in sidebar. */
export const getIsCompetitionsPaneExpanded = createSelector(
  [getDeviceSettingsExpandedViews, getIsAnonymous],
  (views, isAnonymous) =>
    !isAnonymous && views.includes(ViewType.COMPETITIONS_PANE)
);

/** Gets whether the settings pane is visible in sidebar. */
export const getIsSettingsPaneEnabled = createSelector(
  [getDeviceSettingsEnabledViews],
  views => views.includes(ViewType.SETTINGS_PANE)
);

/** Gets whether the settings pane is expanded in sidebar. */
export const getIsSettingsPaneExpanded = createSelector(
  [getDeviceSettingsExpandedViews],
  views => views.includes(ViewType.SETTINGS_PANE)
);

/** Gets whether the debug pane is visible in sidebar. */
export const getIsDebugPaneEnabled = createSelector(
  [getDeviceSettingsEnabledViews, getIsAdmin],
  (views, isAdmin) => isAdmin && views.includes(ViewType.DEBUG_PANE)
);

/** Gets whether the debug pane is expanded in sidebar. */
export const getIsDebugPaneExpanded = createSelector(
  [getDeviceSettingsExpandedViews],
  views => views.includes(ViewType.DEBUG_PANE)
);

/** Gets whether the metric pane is visible in sidebar. */
export const getIsMetricPaneEnabled = createSelector(
  [getDeviceSettingsExpandedViews],
  views => views.includes(ViewType.METRIC_PANE)
);

/** Gets whether the metric pane is expanded in sidebar. */
export const getIsMetricPaneExpanded = createSelector(
  [getDeviceSettingsExpandedViews],
  views => views.includes(ViewType.METRIC_PANE)
);

export const getIsInteractiveSessionsExpanded = createSelector(
  [getDeviceSettingsExpandedViews],
  views => views.includes(ViewType.INTERACTIVE_SESSIONS)
);

export const getIsCommitSessionsExpanded = createSelector(
  [getDeviceSettingsExpandedViews],
  views => views.includes(ViewType.COMMIT_SESSIONS)
);

/** Gets whether the sidebar should be expanded or not. */
export const getIsSidebarExpanded = createSelector(
  [getDeviceSettingsExpandedViews],
  views => views.includes(ViewType.SIDEBAR)
);

/** Gets whether the snippets pane is visible in sidebar. */
export const getIsSnippetsPaneEnabled = createSelector(
  [getDeviceSettingsEnabledViews],
  _ => true
);

/** Gets whether the snippets pane is expanded or not. */
export const getIsSnippetsPaneExpanded = createSelector(
  [getDeviceSettingsExpandedViews],
  views => views.includes(ViewType.SNIPPETS_PANE)
);

/** Gets whether the schedule pane is visible in sidebar. */
export const getIsSchedulePaneEnabled = createSelector(
  [getDeviceSettingsEnabledViews],
  views => views.includes(ViewType.SCHEDULE_PANE)
);

/** Gets whether the schedule pane is expanded or not. */
export const getIsSchedulePaneExpanded = createSelector(
  [getDeviceSettingsExpandedViews],
  views => views.includes(ViewType.SCHEDULE_PANE)
);

/** Gets the state of sidebar panes collectively and controls their ordering. */
export const getSidebarPanes = createSelector(
  [
    getIsDataPaneEnabled,
    getIsModelsPaneEnabled,
    getIsCompetitionsPaneEnabled,
    getIsSettingsPaneEnabled,
    getIsDebugPaneEnabled,
    getIsSnippetsPaneEnabled,
    getIsSchedulePaneEnabled,
    getIsMetricPaneEnabled,
    getIsDataPaneExpanded,
    getIsModelsPaneExpanded,
    getIsCompetitionsPaneExpanded,
    getIsSettingsPaneExpanded,
    getIsDebugPaneExpanded,
    getIsSnippetsPaneExpanded,
    getIsSchedulePaneExpanded,
    getIsMetricPaneExpanded,
    getCurrentIsOwner,
    getIsCompetitionMetric,
  ],
  (
    isDataPaneEnabled: boolean,
    isModelsPaneEnabled: boolean,
    isCompetitionsPaneEnabled: boolean,
    isSettingsEnabled: boolean,
    isDebugEnabled: boolean,
    isSnippetsEnabled: boolean,
    isScheduleEnabled: boolean,
    isMetricEnabled: boolean,
    isDataPaneExpanded: boolean,
    isModelsPaneExpanded: boolean,
    isCompetitionsPaneExpanded: boolean,
    isSettingsExpanded: boolean,
    isDebugExpanded: boolean,
    isSnippetsExpanded: boolean,
    isScheduleExpanded: boolean,
    isMetricExpanded: boolean,
    isOwner: boolean,
    isCompetitionMetric: boolean
  ) => {
    const panes: SidebarViewMetadata[] = [];
    if (isDataPaneEnabled) {
      panes.push({
        viewType: ViewType.DATA_PANE,
        title: `${isCompetitionMetric ? "Testing Data" : "Data"}`,
        isExpanded: isDataPaneExpanded,
      });
    }
    if (isModelsPaneEnabled) {
      panes.push({
        viewType: ViewType.MODELS_PANE,
        title: "Models",
        isExpanded: isModelsPaneExpanded,
      });
    }
    if (isCompetitionsPaneEnabled) {
      panes.push({
        viewType: ViewType.COMPETITIONS_PANE,
        title: "Submit to competition",
        isExpanded: isCompetitionsPaneExpanded,
      });
    }
    if (isSettingsEnabled) {
      panes.push({
        viewType: ViewType.SETTINGS_PANE,
        title: "Settings",
        isExpanded: isSettingsExpanded,
      });
    }
    if (isScheduleEnabled && isOwner && !isCompetitionMetric) {
      panes.push({
        viewType: ViewType.SCHEDULE_PANE,
        title: "Schedule a notebook run",
        isExpanded: isScheduleExpanded,
      });
    }
    if (isDebugEnabled) {
      panes.push({
        viewType: ViewType.DEBUG_PANE,
        title: "Debug",
        isExpanded: isDebugExpanded,
      });
    }
    if (isSnippetsEnabled) {
      panes.push({
        viewType: ViewType.SNIPPETS_PANE,
        title: "Code Help",
        isExpanded: isSnippetsExpanded,
      });
    }
    if (isMetricEnabled) {
      panes.push({
        viewType: ViewType.METRIC_PANE,
        title: "Metric",
        isExpanded: isMetricExpanded,
      });
    }
    return panes;
  }
);
